import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Bulk discounts for one product + duration: "buy 5 or more -> $1.28 each".
 * The bot shows them on the product page and charges the tier price for
 * multi-buys and API orders of that size. Saved immediately (own endpoint).
 *
 * props: productId, durationMonths, title, baseUsd (normal price), costUsd (supplier
 * cost, or 0), tiers (current), onClose(), onSaved(tiers)
 */
function BulkPricesEditor({ productId, durationMonths, title, baseUsd, costUsd = 0, tiers, onClose, onSaved }) {
    const [rows, setRows] = useState(() => (tiers && tiers.length ? tiers : [{ minQty: 5, unitUsd: '' }])
        .map(t => ({ minQty: String(t.minQty), unitUsd: String(t.unitUsd) })));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const base = Number(baseUsd) || 0;

    const set = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
    const add = () => setRows(rs => {
        const last = rs[rs.length - 1];
        const next = last && Number(last.minQty) > 0 ? Math.min(1000, Number(last.minQty) * 2) : 5;
        return [...rs, { minQty: String(next), unitUsd: '' }];
    });
    const remove = i => setRows(rs => rs.filter((_, j) => j !== i));

    const save = async (list) => {
        setBusy(true); setError(null);
        try {
            const res = await fetch(`${API_URL}/admin/telegram/bulk-prices`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
                body: JSON.stringify({ productId, durationMonths, tiers: list })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) { setError(body.error || 'Could not save.'); setBusy(false); return; }
            setBusy(false);
            onSaved && onSaved(body.tiers || []);
            onClose && onClose();
        } catch (e) {
            setError('Could not reach the server — try again.');
            setBusy(false);
        }
    };

    const filled = rows.filter(r => String(r.minQty).trim() !== '' || String(r.unitUsd).trim() !== '');
    const submit = () => save(filled.map(r => ({ minQty: Number(r.minQty), unitUsd: Number(r.unitUsd) })));

    return (
        <div className="tg-modal-back" onClick={onClose}>
            <div className="tg-modal" onClick={e => e.stopPropagation()}>
                <h3 style={{ marginTop: 0 }}>🏷 Bulk prices</h3>
                <p className="tg-sub">
                    <b>{title}</b> — normal price <b>${base.toFixed(2)}</b> each.
                    {costUsd > 0 && <> Supplier cost <b>${Number(costUsd).toFixed(2)}</b>: every bulk price must stay above it.</>}
                    {' '}Buyers who take this many or more in one purchase pay the lower price for every unit.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span className="tg-label" style={{ margin: 0 }}>From quantity</span>
                    <span className="tg-label" style={{ margin: 0 }}>Price per unit ($)</span>
                    <span />
                    <span />
                    {rows.map((r, i) => {
                        const unit = Number(r.unitUsd);
                        const pct = base > 0 && unit > 0 ? Math.round((1 - unit / base) * 100) : null;
                        const bad = unit > 0 && (unit >= base || (costUsd > 0 && unit <= costUsd));
                        return (
                            <React.Fragment key={i}>
                                <input className="tg-input" type="number" min="2" max="1000" step="1" value={r.minQty}
                                    onChange={e => set(i, 'minQty', e.target.value)} placeholder="e.g. 5" />
                                <input className="tg-input" type="number" min="0" step="0.01" value={r.unitUsd}
                                    onChange={e => set(i, 'unitUsd', e.target.value)} placeholder={base ? `below ${base.toFixed(2)}` : 'price'}
                                    style={bad ? { borderColor: '#dc2626' } : undefined} />
                                <span style={{ fontSize: 12, minWidth: 64, color: bad ? '#dc2626' : '#15803d' }}>
                                    {unit > 0 ? (unit >= base ? 'not cheaper' : costUsd > 0 && unit <= costUsd ? 'below cost' : `−${pct}%`) : ''}
                                </span>
                                <button type="button" className="tg-btn sm" onClick={() => remove(i)} title="Remove this tier">✕</button>
                            </React.Fragment>
                        );
                    })}
                </div>
                <button type="button" className="tg-btn sm" onClick={add} disabled={rows.length >= 8}>+ Add tier</button>

                {filled.length > 0 && filled.every(r => Number(r.minQty) >= 2 && Number(r.unitUsd) > 0) && (
                    <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(37,99,235,0.08)', fontSize: 13 }}>
                        <b>Buyers see:</b>
                        <div style={{ marginTop: 4, fontFamily: 'inherit' }}>
                            {[...filled].sort((a, b) => Number(a.minQty) - Number(b.minQty)).map(r => (
                                <div key={r.minQty}>• ≥ {r.minQty} items → <b>${Number(r.unitUsd).toFixed(2)}</b>/unit</div>
                            ))}
                        </div>
                    </div>
                )}

                {error && <div style={{ marginTop: 10, color: '#dc2626', fontSize: 13 }}>{error}</div>}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap' }}>
                    <button type="button" className="tg-btn sm" disabled={busy || !(tiers && tiers.length)}
                        onClick={() => { if (window.confirm('Remove all bulk prices for this option?')) save([]); }}>Remove all</button>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="tg-btn sm" onClick={onClose}>Cancel</button>
                        <button type="button" className="tg-btn primary sm" disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BulkPricesEditor;
